import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export interface ISubmissionApplicationInstanceNavigationParams {
  submissionUuid: string;
  applicationUuid: string;
  submissionApplicationUuid: string;
}

export interface IUnderwritingSubmissionDetailParams {
  submissionUuid: string;
  applicationUuid?: string;
  submissionApplicationUuid?: string;
  sectionUrl?: string;
  subSectionUrl?: string;
}

export interface IInsuredEntityDetailParams {
  insuredEntityUuid: string;
  sectionUrl?: string;
}
export interface IClaimEntityDetailParams {
  claimEntityUuid: string;
  sectionUrl?: string;
}

export interface ICreateClaimEntityParams {
  insuredUuid: string;
  sectionUrl?: string;
}
export interface IBrokerAgencyEntityDetailParams {
  brokerAgencyUuid: string;
  sectionUrl?: string;
}

export interface IQuoteEntityDetailParams {
  quoteUuid: string;
  sectionUrl?: string;
}

export interface IPolicyEntityDetailParams {
  policyUuid: string;
  sectionUrl?: string;
}

export interface IMidtermTransactionWizardParams {
  insuredEntityUuid: string;
  policyUuid: string;
}

export interface BrokerageEntityDetailParams {
  brokerageUuid: string | undefined;
}
export interface BrokerDetailParams {
  brokerUuid: string | undefined;
}

export const ConvergeNavigationUrls = {
  home: () => '/',
  profile: () => '/profile',
  clearance: () => '/clearance',
  underwriting: () => '/underwriting',
  producer: () => '/producers/brokerages',
  brokerage: ({ brokerageUuid }: BrokerageEntityDetailParams) => {
    return `/producers/brokerage/${brokerageUuid}`;
  },
  broker: ({ brokerUuid }: BrokerDetailParams) => {
    return `/producers/broker/${brokerUuid}`;
  },
  producerBrokers: () => '/producers/brokers',
  underwritingCreateSubmission: () => '/underwriting/create-submission',
  submissionApplicationInstance: ({
    submissionUuid,
    applicationUuid,
    submissionApplicationUuid,
  }: ISubmissionApplicationInstanceNavigationParams) =>
    `/submissions/${submissionUuid}/applications/${applicationUuid}/instance/${submissionApplicationUuid}`,
  underwritingSubmissionDetail: ({
    submissionUuid,
    applicationUuid,
    submissionApplicationUuid,
    sectionUrl = 'overview',
    subSectionUrl = '',
  }: IUnderwritingSubmissionDetailParams) => {
    let url = `/underwriting/${submissionUuid}/${sectionUrl}`;

    if (sectionUrl === 'questionnaire') {
      if (applicationUuid && submissionApplicationUuid) {
        url += `/applications/${applicationUuid}/instance/${submissionApplicationUuid}`;
      }

      if (subSectionUrl) {
        url += `/${subSectionUrl}`;
      }
    }

    return url;
  },
  insuredEntityDetail: ({
    insuredEntityUuid,
    sectionUrl = 'overview',
  }: IInsuredEntityDetailParams) => {
    return `/insureds/${insuredEntityUuid}/${sectionUrl}`;
  },
  claimEntityDetail: ({
    claimEntityUuid,
    sectionUrl = 'details',
  }: IClaimEntityDetailParams) => {
    return `/claims/${claimEntityUuid}/${sectionUrl}`;
  },
  createClaim: ({ insuredUuid }: ICreateClaimEntityParams) =>
    `/insureds/${insuredUuid}/create-claim`,
  brokerAgencyEntityDetail: ({
    brokerAgencyUuid,
    sectionUrl = 'overview',
  }: IBrokerAgencyEntityDetailParams) => {
    return `/broker-agencies/${brokerAgencyUuid}/${sectionUrl}`;
  },
  quoteEntityDetail: ({
    quoteUuid,
    sectionUrl = 'overview',
  }: IQuoteEntityDetailParams) => {
    return `/quotes/${quoteUuid}/${sectionUrl}`;
  },
  policyEntityDetail: ({
    policyUuid,
    sectionUrl = 'overview',
  }: IPolicyEntityDetailParams) => {
    return `/policies/${policyUuid}/${sectionUrl}`;
  },
  midtermTransactionWizard: ({
    insuredEntityUuid,
    policyUuid,
  }: IMidtermTransactionWizardParams) => {
    return `/insureds/${insuredEntityUuid}/policies/${policyUuid}/midterm-transaction`;
  },
};

export const ConvergeMatchUrls = {
  underwritingSubmissionDetail: () =>
    '/underwriting/:submissionUuid/:sectionId',
  underwritingSubmissionDetailSubSection: () =>
    '/underwriting/:submissionUuid/:sectionId/applications/:applicationUuid/instance/:submissionApplicationUuid/:subSectionId',
  insuredEntityDetail: () => '/insureds/:insuredUuid/:sectionId',
  claimEntityDetail: () => '/claims/:claimUuid/:sectionId',
  brokerAgencyEntityDetail: () =>
    '/broker-agency/:brokerAgencyEntityUuid/:sectionId',
  quoteEntityDetail: () => '/quote/:quoteEntityUuid/:sectionId',
  policyEntityDetail: () => '/policy/:policyEntityUuid/:sectionId',
};

export const useConvergeNavigation = () => {
  const navigate = useNavigate();

  return useMemo(() => {
    return {
      home: () => navigate(ConvergeNavigationUrls.home()),
      profile: () => navigate(ConvergeNavigationUrls.profile()),
      dashboard: () => navigate(ConvergeNavigationUrls.clearance()),
      underwriting: () => navigate(ConvergeNavigationUrls.underwriting()),
      brokerage: (params: BrokerageEntityDetailParams) =>
        navigate(ConvergeNavigationUrls.brokerage(params)),
      broker: (params: BrokerDetailParams) =>
        navigate(ConvergeNavigationUrls.broker(params)),
      producerBrokers: () => navigate(ConvergeNavigationUrls.producerBrokers()),
      producer: () => navigate(ConvergeNavigationUrls.producer()),
      underwritingCreateSubmission: () =>
        navigate(ConvergeNavigationUrls.underwritingCreateSubmission()),
      createClaim: (params: ICreateClaimEntityParams) =>
        navigate(ConvergeNavigationUrls.createClaim(params)),
      submissionApplicationInstance: (
        params: ISubmissionApplicationInstanceNavigationParams
      ) =>
        navigate(ConvergeNavigationUrls.submissionApplicationInstance(params)),
      underwritingSubmissionDetail: (
        params: IUnderwritingSubmissionDetailParams
      ) =>
        navigate(ConvergeNavigationUrls.underwritingSubmissionDetail(params)),
      insuredEntityDetail: (params: IInsuredEntityDetailParams) =>
        navigate(ConvergeNavigationUrls.insuredEntityDetail(params)),
      claimEntityDetail: (params: IClaimEntityDetailParams) =>
        navigate(ConvergeNavigationUrls.claimEntityDetail(params)),
      brokerAgencyEntityDetail: (params: IBrokerAgencyEntityDetailParams) =>
        navigate(ConvergeNavigationUrls.brokerAgencyEntityDetail(params)),
      quoteEntityDetail: (params: IQuoteEntityDetailParams) =>
        navigate(ConvergeNavigationUrls.quoteEntityDetail(params)),
      policyEntityDetail: (params: IPolicyEntityDetailParams) =>
        navigate(ConvergeNavigationUrls.policyEntityDetail(params)),
    };
  }, [navigate]);
};
